import React from 'react';
import { Toggle } from '../Toggle/Toggle';
import { formatThousands } from '@/utils/format';

/** White card with a titled header, used for each form section. */
export const FormCard: React.FC<{
  title: string;
  /** Right side of the header, e.g. a status chip. */
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, action, children }) => (
  <div className="bg-white rounded-xl border border-gray-200">
    <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
      <h2 className="text-[16px] font-medium text-gray-900">{title}</h2>
      {action}
    </div>
    {children}
  </div>
);

export const FieldLabel: React.FC<{
  children: React.ReactNode;
  required?: boolean;
  hint?: string;
}> = ({ children, required = false, hint }) => (
  <label className="block text-[14px] text-gray-900 mb-1.5">
    {children}
    {required && <span className="text-primary ml-0.5">*</span>}
    {hint && <span className="text-gray-500 ml-1">{hint}</span>}
  </label>
);

/**
 * `text-base` (16px) below `md` stops iOS Safari auto-zooming on focus;
 * `md:text-[14px]` keeps the desktop size. Same rule for every control here.
 */
export const inputClass = (hasError?: boolean) =>
  `w-full px-3 py-2.5 border rounded-lg text-base md:text-[14px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-primary ${
    hasError ? 'border-red-500' : 'border-gray-200'
  }`;

/**
 * Text input with a static "Rp" prefix, grouped for readability as you type
 * ("100000" shows as "100.000").
 *
 * Only the display is formatted — `onChange` emits raw digits, because
 * Number("100.000") is 100 and a grouped string reaching state would silently
 * shrink the amount by three orders of magnitude.
 */
export const RupiahInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hasError?: boolean;
}> = ({ value, onChange, placeholder, hasError }) => (
  <div
    className={`flex items-center border rounded-lg px-3 focus-within:ring-1 focus-within:ring-primary ${
      hasError ? 'border-red-500' : 'border-gray-200'
    }`}
  >
    <span className="text-[14px] text-gray-500 mr-2">Rp</span>
    <input
      type="text"
      inputMode="numeric"
      value={formatThousands(value)}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
      placeholder={placeholder ? formatThousands(placeholder) : undefined}
      className="flex-1 py-2.5 text-base md:text-[14px] text-gray-900 placeholder:text-gray-400 focus:outline-none"
    />
  </div>
);

/** Native select styled to match the design's dropdown fields. */
export const SelectField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}> = ({
  value,
  onChange,
  options,
  placeholder,
  hasError,
  disabled = false,
  className = '',
  ariaLabel,
}) => (
  <div className={`relative ${className}`}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      disabled={disabled}
      className={`w-full appearance-none px-3 py-2.5 pr-9 border rounded-lg text-base md:text-[14px] focus:outline-none focus:ring-1 focus:ring-primary ${
        hasError ? 'border-red-500' : 'border-gray-200'
      } ${value ? 'text-gray-900' : 'text-gray-400'} ${
        disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'
      }`}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-[18px] text-gray-400 pointer-events-none"></i>
  </div>
);

/** Toggle row: bold label with the switch beside it, helper text beneath. */
export const ToggleRow: React.FC<{
  label: string;
  helper?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, helper, checked, onChange }) => (
  <div className="mb-4">
    <div className="flex items-center gap-2">
      <span className="text-[14px] text-gray-900">{label}</span>
      <Toggle checked={checked} onChange={onChange} ariaLabel={label} />
    </div>
    {helper && <p className="mt-1 text-[13px] text-gray-500">{helper}</p>}
  </div>
);

export const FieldHelper: React.FC<{
  children: React.ReactNode;
  withIcon?: boolean;
}> = ({ children, withIcon = false }) => (
  <p className="mt-1.5 flex items-start gap-1 text-[13px] text-gray-500">
    {withIcon && (
      <i className="ri-information-line text-[14px] leading-[20px]"></i>
    )}
    <span>{children}</span>
  </p>
);

export const FieldError: React.FC<{ children?: string }> = ({ children }) =>
  children ? <p className="mt-1 text-[12px] text-red-500">{children}</p> : null;

/** Outlined primary "+ label" button used to append repeatable rows. */
export const AddRowButton: React.FC<{
  label: string;
  onClick: () => void;
}> = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-2 px-4 py-2.5 border border-primary rounded-lg text-[14px] font-medium text-primary hover:bg-purple-50"
  >
    <i className="ri-add-line text-[16px]"></i>
    {label}
  </button>
);

/** Character counter shown bottom-right inside a textarea shell. */
export const TextareaField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  rows?: number;
  placeholder?: string;
  /** Lets a `<label htmlFor>` name the textarea. */
  id?: string;
}> = ({ value, onChange, maxLength = 200, rows = 4, placeholder, id }) => (
  <div className="relative">
    <textarea
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      maxLength={maxLength}
      rows={rows}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 pb-7 border border-gray-200 rounded-lg text-base md:text-[14px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-primary resize-y"
    />
    <span className="absolute bottom-2.5 right-3 text-[12px] text-gray-400">
      {value.length}/{maxLength}
    </span>
  </div>
);
